'use client';

import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, Lock } from 'lucide-react';

interface TabItem {
  value: string;
  title: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
}

interface ProjectSidebarLayoutProps {
  tabItems: TabItem[];
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  isReadOnly?: boolean;
}

export function ProjectSidebarLayout({
  tabItems,
  defaultValue,
  value,
  onValueChange,
  className,
  isReadOnly,
}: ProjectSidebarLayoutProps) {
  const [activeTab, setActiveTab] = React.useState(defaultValue || tabItems[0]?.value);
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  const selectedTab = value ?? activeTab;

  React.useEffect(() => {
    if (value === undefined && defaultValue && tabItems.some((tab) => tab.value === defaultValue)) {
      setActiveTab(defaultValue);
    }
  }, [defaultValue, tabItems, value]);


  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '[' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsCollapsed((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <TooltipPrimitive.Provider delayDuration={100}>
      <TabsPrimitive.Root
        orientation="vertical"
        value={selectedTab}
        onValueChange={(nextTab) => {
          setActiveTab(nextTab);
          onValueChange?.(nextTab);
        }}
        className={cn('flex w-full items-start gap-0', className)}
      >

        <aside
          className={cn(
            'self-start sticky top-20 shrink-0 flex flex-col transition-[width] duration-300 ease-in-out',
            isCollapsed ? 'w-[68px]' : 'w-60'
          )}
          style={{ maxHeight: 'calc(100vh - 6rem)' }}
        >
          <div className="flex flex-col h-full rounded-2xl border border-border/30 bg-card/60 backdrop-blur-xl shadow-xl shadow-black/5 overflow-hidden">

            {/* Header */}
            <div
              className={cn(
                'shrink-0 flex items-center border-b border-border/20 transition-all duration-300',
                isCollapsed ? 'justify-center p-3' : 'justify-between px-4 py-3.5'
              )}
            >
              {!isCollapsed && (
                <div className="flex items-center gap-2 overflow-hidden">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/50 whitespace-nowrap">
                    Navigation
                  </span>
                </div>
              )}
              <button
                onClick={() => setIsCollapsed((v) => !v)}
                className={cn(
                  'flex items-center justify-center rounded-xl transition-all duration-200',
                  'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                  isCollapsed ? 'w-9 h-9' : 'w-7 h-7'
                )}
                title={isCollapsed ? 'Expand (⌘[)' : 'Collapse (⌘[)'}
              >
                {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </button>
            </div>

            {/* Tab list — internal scroll only if too many tabs */}
            <TabsPrimitive.List className="flex flex-col gap-1 p-2 overflow-y-auto">
              {tabItems.map((item) => {
                const isActive = selectedTab === item.value;

                const trigger = (
                  <TabsPrimitive.Trigger
                    key={item.value}
                    value={item.value}
                    className={cn(
                      'group relative w-full flex items-center gap-3 text-[12px] font-semibold',
                      'transition-all duration-200 rounded-xl outline-none select-none cursor-pointer',
                      isCollapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5',
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    )}
                  >
                    {isActive && (
                      <span className="absolute inset-0 rounded-xl bg-primary/15 blur-sm -z-10" />
                    )}
                    <span
                      className={cn(
                        'shrink-0 transition-transform duration-200',
                        isActive ? 'text-primary-foreground scale-110' : 'text-primary group-hover:scale-110',
                        isCollapsed && 'mx-auto'
                      )}
                    >
                      {item.icon}
                    </span>
                    {!isCollapsed && <span className="truncate">{item.title}</span>}
                    {isActive && !isCollapsed && (
                      <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary-foreground/60 shrink-0" />
                    )}
                  </TabsPrimitive.Trigger>
                );

                if (isCollapsed) {
                  return (
                    <TooltipPrimitive.Root key={item.value}>
                      <TooltipPrimitive.Trigger asChild>{trigger}</TooltipPrimitive.Trigger>
                      <TooltipPrimitive.Portal>
                        <TooltipPrimitive.Content
                          side="right"
                          sideOffset={12}
                          className="z-50 rounded-lg px-3 py-1.5 text-[11px] font-bold shadow-xl bg-popover text-popover-foreground border border-border/40 backdrop-blur-xl animate-in fade-in zoom-in-95"
                        >
                          {item.title}
                          <TooltipPrimitive.Arrow className="fill-popover" />
                        </TooltipPrimitive.Content>
                      </TooltipPrimitive.Portal>
                    </TooltipPrimitive.Root>
                  );
                }
                return trigger;
              })}
            </TabsPrimitive.List>

            {/* Read-only footer */}
            {isReadOnly && !isCollapsed && (
              <div className="shrink-0 m-2 p-3 rounded-xl bg-primary/5 border border-primary/10 relative overflow-hidden">
                <div className="flex items-center gap-2 mb-1">
                  <Lock className="h-3 w-3 text-primary shrink-0" />
                  <p className="text-[9px] font-black text-primary uppercase tracking-widest">Read-Only</p>
                </div>
                <p className="text-[9px] text-muted-foreground/60 leading-relaxed">
                  Previewing this project. Edit actions are disabled.
                </p>
                <div className="absolute -right-3 -bottom-3 w-12 h-12 rounded-full bg-primary/10" />
              </div>
            )}
            {isReadOnly && isCollapsed && (
              <div className="shrink-0 flex justify-center pb-3">
                <TooltipPrimitive.Root>
                  <TooltipPrimitive.Trigger asChild>
                    <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary/5 border border-primary/10 text-primary cursor-default">
                      <Lock className="h-3.5 w-3.5" />
                    </div>
                  </TooltipPrimitive.Trigger>
                  <TooltipPrimitive.Portal>
                    <TooltipPrimitive.Content
                      side="right"
                      sideOffset={12}
                      className="z-50 rounded-lg px-3 py-1.5 text-[11px] font-bold shadow-xl bg-popover text-popover-foreground border border-border/40 backdrop-blur-xl"
                    >
                      Read-Only View
                    </TooltipPrimitive.Content>
                  </TooltipPrimitive.Portal>
                </TooltipPrimitive.Root>
              </div>
            )}
          </div>
        </aside>

        <div className="self-stretch w-px shrink-0 bg-gradient-to-b from-transparent via-border/30 to-transparent mx-5" />

        <div className="flex-1 min-w-0 pb-16">
          {tabItems.map((item) => (
            <TabsPrimitive.Content
              key={item.value}
              value={item.value}
              className="w-full outline-none data-[state=inactive]:hidden animate-in fade-in-0 slide-in-from-bottom-2 duration-300"
            >
              {item.content}
            </TabsPrimitive.Content>
          ))}
        </div>
      </TabsPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
